const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

/**
 * Obtener solicitudes de lencería (prendas) con filtros
 */
export const fetchSolicitudesPrendas = async (filters = {}) => {
  try {
    const response = await fetch(`${API_URL}/solicitud-prendas/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        limit: filters.limit || 100,
        offset: filters.offset || 1,
        query: filters.query || {},
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener solicitudes de prendas');
    }

    const result = await response.json();
    
    // La respuesta viene como { data: [...], totalRecords: n }
    return {
      solicitudes: result.data || [],
      total: result.totalRecords || 0,
    };
  } catch (error) {
    console.error('Error en fetchSolicitudesPrendas:', error);
    throw error;
  }
};

/**
 * Obtener solicitudes de insumos con filtros
 */
export const fetchSolicitudesInsumos = async (filters = {}) => {
  try {
    const response = await fetch(`${API_URL}/solicitud-insumos/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        limit: filters.limit || 100,
        offset: filters.offset || 1,
        query: filters.query || {},
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener solicitudes de insumos');
    }

    const result = await response.json();
    
    // La respuesta viene como { data: [...], totalRecords: n }
    return {
      solicitudes: result.data || [],
      total: result.totalRecords || 0,
    };
  } catch (error) {
    console.error('Error en fetchSolicitudesInsumos:', error);
    throw error;
  }
};

/**
 * Obtener solicitudes pendientes (lencería + insumos) del día actual
 */
export const fetchSolicitudesPendientes = async () => {
  try {
    // Calcular rango del día actual (00:00:00 - 23:59:59)
    const hoy = new Date();
    const inicioDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 0, 0, 0, 0);
    const finDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59, 999);

    // Obtener solicitudes de lencería pendientes del día actual
    const prendasResponse = await fetchSolicitudesPrendas({
      query: { 
        estado: 'Pendiente',
        fechaEntrega: {
          $gte: inicioDia.toISOString(),
          $lte: finDia.toISOString()
        }
      },
      limit: 100,
    });

    // Obtener solicitudes de insumos pendientes del día actual
    const insumosResponse = await fetchSolicitudesInsumos({
      query: { 
        estado: 'Pendiente',
        fechaEntrega: {
          $gte: inicioDia.toISOString(),
          $lte: finDia.toISOString()
        }
      },
      limit: 100,
    });

    // Combinar y agregar tipo
    const prendasConTipo = (prendasResponse.solicitudes || []).map(s => ({
      ...s,
      tipo: 'lenceria',
    }));

    const insumosConTipo = (insumosResponse.solicitudes || []).map(s => ({
      ...s,
      tipo: 'insumos',
    }));

    // Combinar ambas listas
    const todasSolicitudes = [...prendasConTipo, ...insumosConTipo];

    // Ordenar por fecha de creación (más recientes primero)
    todasSolicitudes.sort((a, b) => {
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    return {
      solicitudes: todasSolicitudes,
      total: todasSolicitudes.length,
      totalPrendas: prendasConTipo.length,
      totalInsumos: insumosConTipo.length,
    };
  } catch (error) {
    console.error('Error en fetchSolicitudesPendientes:', error);
    throw error;
  }
};

/**
 * Obtener solicitudes verticales pendientes (para recolección de ropa)
 */
export const fetchSolicitudesVerticalesPendientes = async () => {
  try {
    const response = await fetch(`${API_URL}/solicitud-prendas-vertical/filter`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        limit: 1000,
        offset: 1,
        query: { estado: 'Pendiente' },
      }),
    });

    if (!response.ok) {
      throw new Error('Error al obtener solicitudes verticales pendientes');
    }

    const result = await response.json();
    
    return {
      solicitudes: result.data || [],
      total: result.totalRecords || 0,
    };
  } catch (error) {
    console.error('Error en fetchSolicitudesVerticalesPendientes:', error);
    throw error;
  }
};

/**
 * Completar todas las solicitudes pendientes de un edificio
 * @param {string} edificioId - ID del edificio
 * @param {object} recoleccionData - Datos de la recolección
 */
export const completarSolicitudesPorEdificio = async (edificioId, recoleccionData = {}) => {
  try {
    const response = await fetch(`${API_URL}/solicitud-prendas-vertical/completar-por-edificio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        edificioId,
        recolectadoPor: recoleccionData.recolectadoPor,
        fechaRecoleccion: recoleccionData.fechaRecoleccion || new Date(),
      }),
    });

    if (!response.ok) {
      throw new Error('Error al completar solicitudes del edificio');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error en completarSolicitudesPorEdificio:', error);
    throw error;
  }
};

export const updateSolicitudPrendaEstado = async (id, estado = 'Pendiente', isExtraRuta = true) => {
  try {
    const response = await fetch(`${API_URL}/solicitud-prendas/actualizar-estado`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, estado, isExtraRuta }),
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(result.message || 'Error al actualizar estado de la solicitud');
    }

    return result;
  } catch (error) {
    console.error('Error en updateSolicitudPrendaEstado:', error);
    throw error;
  }
};
